import { ChangeEventHandler, forwardRef, useRef } from 'react'

import { Handler } from '../../helpers/typeHelper'
import { mergeRefs } from '../../helpers/utils'
import { DateIcon, ErrorMessage, Field, HelperMessage, InputStyled, Label } from './DateInput.styles'

export interface DateInputProps {
  onChange?: Handler | ChangeEventHandler<HTMLInputElement>
  value?: string
  label?: string
  name?: string
  error?: React.ReactElement | string | boolean
  helperText?: string
  shouldNotForwardRef?: boolean
  noMargin?: boolean
  backgroundColor?: string
}

export const DateInput = forwardRef(
  (
    {
      onChange,
      value,
      label,
      name,
      error,
      helperText,
      shouldNotForwardRef,
      noMargin,
      backgroundColor,
    }: DateInputProps,
    ref
  ) => {
    const inputDateField = useRef<HTMLInputElement>(null)
    const hasError = Boolean(error)
    const hasValue = Boolean(value)

    function handleFocus() {
      if (name) {
        inputDateField?.current?.focus()
      }
    }

    return (
      <Field
        onClick={handleFocus}
        $hasError={hasError}
        $noMargin={Boolean(noMargin)}
        $backgroundColor={backgroundColor}
      >
        <DateIcon $active={hasValue} />
        <InputStyled
          min="1900-01-01"
          max="2200-12-31"
          // React hook form auto focuses on error, with no easy way to turn it off for just a single input, so we do not forward refs if we want to disable auto focus
          ref={shouldNotForwardRef ? inputDateField : mergeRefs([inputDateField, ref])}
          type="date"
          name={name}
          id={name}
          onChange={onChange}
          value={value}
          $error={hasError}
          $active={hasValue}
        />
        <Label htmlFor={name} $active={hasValue}>
          {label}
        </Label>
        {hasError && error !== true && <ErrorMessage>{error}</ErrorMessage>}
        {!hasError && helperText && <HelperMessage>{helperText}</HelperMessage>}
      </Field>
    )
  }
)
